import React, { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import { CsrLoginSessionSource, useCreateCsrCustomerMagicLinkMutation } from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';
import { getErrorMessage } from '@occc/utils';

import { OpenExperienceModal } from './open-experience.modal';

type OpenExperienceModalContainerProps = {
  isOpen: boolean;
  onHide: () => void;
  memberPhoneNumber: string;
  smsURL?: string;
  isOTPFlow: boolean;
};

const onNoUrlToOpen = () => {
  snackbar.error('Something went wrong. Please try again.');
};
const onOpenExperienceWindowNotOpenedError = () => {
  snackbar.error('Could not open Experience App. Please check popup settings.');
};
const onError = (error: ApolloError) => {
  snackbar.error(getErrorMessage(error));
};

export const OpenExperienceModalContainer = ({
  isOpen,
  onHide,
  memberPhoneNumber,
  smsURL,
  isOTPFlow,
}: OpenExperienceModalContainerProps) => {
  const [createCsrCustomerMagicLink, { loading: isLoading }] =
    useCreateCsrCustomerMagicLinkMutation({
      variables: {
        input: {
          customerIdentifier: memberPhoneNumber,
          source: CsrLoginSessionSource.CommandCenterUser,
        },
      },
      onError,
    });

  const onOpenExperienceClick = useCallback(async () => {
    let urlToOpen: string | undefined;

    if (isOTPFlow) {
      urlToOpen = smsURL;
    } else {
      const { data } = await createCsrCustomerMagicLink();
      urlToOpen = data?.createCSRCustomerMagicLink.url;
    }

    if (!urlToOpen) {
      onNoUrlToOpen();
      return;
    }

    const newWindow = window.open(urlToOpen, '_blank');

    if (!newWindow) {
      onOpenExperienceWindowNotOpenedError();
      return;
    }

    newWindow.focus();
    onHide();
  }, [isOTPFlow, onHide, smsURL, createCsrCustomerMagicLink]);

  return (
    <OpenExperienceModal
      isLoading={isLoading}
      isOpen={isOpen}
      onHide={onHide}
      onOpenExperienceClick={onOpenExperienceClick}
    />
  );
};
